// DS: STU3.6
import type { FC } from 'react';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import DndSortableList from '@wix/da-shared-react/pkg/Dnd/DndSortableList';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import {
  BiData,
  BiContextProvidedValue,
  ItemClickBiEvent,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import Slider from '@wix/da-ds/pkg/Slider';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Loader from '@wix/da-ds/pkg/Loader';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import CreateFolderCard from '../../_partials/CreateFolderCard';
import FolderCard from '../../_partials/FolderCard';

import s from './FolderListDesktop.scss';

export const THUMB_HEIGHT = 95;
export const THUMB_WIDTH = 200;
const THUMB_GAP = 16;

export interface Props {
  streamId: string;
  featuredFolderId?: number;
  measuredCookieType?: MeasuredCookieType;
  selectedFolder?: PapiGallection;
  updateFolderOrder?: (folder: PapiGallection, position: number) => void;
  withCreateButton?: boolean;
  withFolderActions?: boolean;
  withHeader?: boolean;
  withDragToOrder?: boolean;
  withCollapseToggle?: boolean;
  expandedByDefault?: boolean;
}

const FolderList: FC<Props> = ({
  streamId,
  featuredFolderId,
  measuredCookieType,
  expandedByDefault,
  selectedFolder,
  withCreateButton,
  withFolderActions,
  withHeader,
  withDragToOrder,
  withCollapseToggle = true,
  updateFolderOrder,
}) => {
  const { t } = useTranslation();
  const { items, isFetching, hasMore, fetchNext } = useStream(streamId || '');
  const { ref: containerRef, dimensions: measuredContainer } = useMeasureElementCookie(
    measuredCookieType,
    {
      cookieDim: 'width',
    }
  );

  const containerWidth = useMemo(() => measuredContainer?.width, [measuredContainer?.width]);

  const thumbsPerRow = useMemo(() => {
    return Math.floor(containerWidth / (THUMB_WIDTH + THUMB_GAP));
  }, [containerWidth]);

  const gapWidth = THUMB_GAP * (thumbsPerRow - 1);

  const thumbWidth = useMemo(
    () => Math.floor((containerWidth - gapWidth) / thumbsPerRow),
    [containerWidth, gapWidth, thumbsPerRow]
  );

  const gridWidth = useMemo(
    () => thumbWidth * thumbsPerRow + gapWidth,
    [thumbsPerRow, thumbWidth, gapWidth]
  );

  const [isFolderListExpanded, expandFolderList] = useState(expandedByDefault);
  const canExpandFolders = withCollapseToggle && items.length > thumbsPerRow;

  const onFolderDragOrder = useCallback(
    (item, position) => updateFolderOrder && updateFolderOrder(item.folder, position - 1),
    [updateFolderOrder]
  );

  const sortableItems = useMemo(
    () => items.map(folder => ({ id: folder.folderId, folder })),
    [items]
  );

  if (items.length < 1) {
    return null;
  }

  const renderFolderCard = (folder: PapiGallection) =>
    folder.isHidden ? null : (
      <FolderCard
        key={`folder-${folder.folderId}`}
        folder={folder}
        width={thumbWidth}
        height={THUMB_HEIGHT}
        withActions={withFolderActions}
        biData={BiData<ItemClickBiEvent>({
          evid: 100,
          view: BiContextProvidedValue,
          component: BiContextProvidedValue,
          page_typeid: BiContextProvidedValue,
          page_itemid: BiContextProvidedValue,
          sectionname: BiContextProvidedValue,
          typeid: ResourceType.GALLERY,
          itemid: folder.folderId,
        })}
        showLockIcon={folder.folderId === featuredFolderId}
      />
    );

  const folderCards = [
    withCreateButton && (
      <CreateFolderCard
        key="folder-create"
        folderType="gallery"
        width={thumbWidth}
        height={THUMB_HEIGHT}
      />
    ),
    withDragToOrder ? (
      <DndSortableList
        key="folder-cards"
        dndType={DraggableType.STUDIO_FOLDER}
        items={sortableItems}
        onDragOrder={onFolderDragOrder}
      >
        {item => renderFolderCard(item.folder)}
      </DndSortableList>
    ) : (
      items.map(folder => renderFolderCard(folder))
    ),
  ];

  const willCreateSubfolder = (selectedFolder?.folderId || 0) > 0;

  return (
    <BiLoggerContextProvider value={{ sectionname: 'subfolders' }}>
      <div ref={containerRef}>
        <div style={{ width: gridWidth }}>
          {withHeader && (
            <div className={s['header']}>
              <Heading className={s['heading']}>
                {willCreateSubfolder
                  ? t('studio.published.folderList.heading.subfolders')
                  : t('studio.published.folderList.heading')}
              </Heading>
              {canExpandFolders && (
                <TextButton
                  biData={
                    isFolderListExpanded || !selectedFolder
                      ? undefined
                      : BiData<DefaultBiEvent>({
                          evid: 2,
                          typeid: gallectionTypeToResourceType(selectedFolder.type),
                          itemid: selectedFolder.folderId,
                          click_info: `${selectedFolder.type}_folder_subfolders_show_more`,
                        })
                  }
                  className={s['more-folders-btn']}
                  size="small"
                  variant="main"
                  onClick={() => expandFolderList(!isFolderListExpanded)}
                >
                  {t(
                    isFolderListExpanded
                      ? 'studio.table.actions.showLess'
                      : 'studio.table.actions.showMore'
                  )}
                </TextButton>
              )}
            </div>
          )}

          <div className={s['content']}>
            {isFolderListExpanded ? (
              <div className={s['expanded-grid']}>{folderCards}</div>
            ) : (
              <Slider contentClassName={s['slider-content']} arrowSize={IconSize.TINY} permArrows>
                {folderCards}
              </Slider>
            )}

            {isFolderListExpanded && hasMore && !isFetching ? (
              <div className={s['load-more']}>
                <TextButton size="medium" variant="main" onClick={() => fetchNext()}>
                  {t('studio.folderList.loadMore')}
                </TextButton>
              </div>
            ) : null}

            {isFetching && <Loader className={s['loader']} state="loading" size="big" />}
          </div>
        </div>
      </div>
    </BiLoggerContextProvider>
  );
};
FolderList.displayName = 'FolderList';

export default FolderList;
